import { createContext, useState, useEffect } from 'react';
import { FavoritesPanelType, ISliderContext, KeyContactsPanelType, LinksPanelType } from './Models/Panels';
import { GetFavorites, IUserFavorite, AddFavorite, RemoveFavorite } from 'Feature/Slider/SliderApi';
import { useUserData } from 'Foundation/User/UserContext';
import { IKeyContact } from './Cards/KeyContactsPanel';
import { ToastContainer, Toast } from '@lmig/lmds-react-toast';
import { Link } from '@lmig/lmds-react-link';
import './Toast.scss';
import { useLocalStorage } from 'Utilities/UseStorage';

type IToast = {
  isOpen: boolean;
  type: ConfirmationType;
  id?: string;
};

export enum ConfirmationType {
  RemoveFavorite = 'Remove',
  AddFavorite = 'Add',
  AddFavoriteOverflow = 'Overflow',
}

const initialState: ISliderContext = {
  favorites: [],
  addFavorite: () => {},
  removeFavorite: () => {},
  setFavorites: () => {},
  setContacts: () => {},
  contactsLoading: false,
  setContactsLoading: () => {},
  panels: undefined,
  contactPanel: undefined,
  addPanel: () => {},
  addContactsPanel: () => {},
  contacts: [],
};

export const SliderContext = createContext(initialState);

export function SliderContextProvider({ children }: { children: React.ReactNode }) {
  const [contacts, setContacts] = useState<IKeyContact[]>();
  const [contactsLoading, setContactsLoading] = useState<boolean>();
  const [panels, setPanels] = useState<(FavoritesPanelType | KeyContactsPanelType | LinksPanelType)[]>();
  const [contactsPanel, setContactsPanel] = useState<KeyContactsPanelType>();
  const { userData } = useUserData();
  const [favorites, setFavorites] = useLocalStorage<string[]>(`favorites-${userData?.dpecid}`, [], 30);
  const [toasts, setToasts] = useState<IToast[]>([]);

  useEffect(() => {
    const getFavoritesAsync = async () => {
      if (!favorites) {
        let favs = await GetFavorites();
        let favIds: string[] = favs.map((item: IUserFavorite) => item.Id);
        setFavorites({ data: favIds });
      }
    };
    if (!favorites) {
      getFavoritesAsync();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let shared: ISliderContext = {
    favorites: favorites,
    addFavorite: async (id: string | undefined) => {
      if (id && userData) {
        let favoriteRec: IUserFavorite = {
          Id: id,
        };
        await AddFavorite(favoriteRec);
        let newFavorites = [...(favorites ?? []), id.toLowerCase()];
        setFavorites({ data: newFavorites });
        if (newFavorites.length > 8)
          setToasts((toasts) => [...toasts, { type: ConfirmationType.AddFavoriteOverflow, isOpen: true, id: id }]);
        else setToasts((toasts) => [...toasts, { type: ConfirmationType.AddFavorite, isOpen: true, id: id }]);
      }
    },
    removeFavorite: async (id: string | undefined) => {
      if (id && userData) {
        await RemoveFavorite(id);
        let newFavorites = (favorites ?? []).filter((x) => x !== id.toLowerCase());
        setFavorites({ data: newFavorites });
        setToasts((toasts) => [...toasts, { type: ConfirmationType.RemoveFavorite, isOpen: true, id: id }]);
      }
    },
    setFavorites: (ids: string[]) => setFavorites({ data: ids }),
    contacts: contacts,
    setContacts: (contacts: IKeyContact[]) => setContacts(contacts),
    contactsLoading: contactsLoading,
    setContactsLoading: (loading: boolean) => setContactsLoading(loading),
    panels: panels,
    addPanel: (panel: FavoritesPanelType | KeyContactsPanelType | LinksPanelType) => {
      setPanels((panels) => [...(panels ?? []), panel]);
    },
    contactPanel: contactsPanel,
    addContactsPanel: (panel: KeyContactsPanelType) => {
      setContactsPanel(panel);
    },
  };

  return (
    <>
      {' '}
      <ToastContainer position="top-right">
        {toasts.map((toast, index) =>
          toast.type == ConfirmationType.AddFavorite ? (
            <Toast
              highlightType="positive"
              toastTitle="Item has been successfully added to your favorites"
              onDismiss={() => setToasts((toasts) => toasts.map((t, i) => (i == index ? { ...t, isOpen: false } : t)))}
              isOpen={toast.isOpen}
              autoDismiss={true}
              animationDirection="left"
              isDismissible={true}
            >
              Click here to <Link href="/manage-my-favorites">manage favorites</Link>.
            </Toast>
          ) : toast.type == ConfirmationType.AddFavoriteOverflow ? (
            <Toast
              highlightType="caution"
              toastTitle="Additional favorites are not visible on the my favorites tile"
              onDismiss={() => setToasts((toasts) => toasts.map((t, i) => (i == index ? { ...t, isOpen: false } : t)))}
              isOpen={toast.isOpen}
              autoDismiss={true}
              animationDirection="left"
              isDismissible={true}
            >
              Control the display of links using <Link href="/manage-my-favorites">manage all favorites</Link>.
            </Toast>
          ) : (
            <Toast
              highlightType="caution"
              toastTitle="Item has been removed from your favorites"
              onDismiss={() => setToasts((toasts) => toasts.map((t, i) => (i == index ? { ...t, isOpen: false } : t)))}
              isOpen={toast.isOpen}
              autoDismiss={true}
              animationDirection="left"
              isDismissible={true}
            >
              Are you sure you want to remove this favorite?{' '}
              <Link
                onClick={() => {
                  setToasts((x) => x.filter((y) => y.id != toast.id));
                  shared.addFavorite(toast.id, '');
                }}
              >
                Undo
              </Link>
              .
            </Toast>
          ),
        )}
      </ToastContainer>
      <SliderContext.Provider value={shared}>{children}</SliderContext.Provider>
    </>
  );
}
